import { Button, Space } from "antd";
import { ColumnsType } from "antd/es/table";
import { useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import AddIcon from "../../../assets/icons/AddIcon";
import ArchiveIcon from "../../../assets/icons/ArchiveIcon";
import EditIcon from "../../../assets/icons/EditIcon";
import ViewIcon from "../../../assets/icons/ViewIcon";
import ActionButton from "../../../components/ui/ActionButton";
import ConfirmModal from "../../../components/ui/ConfirmModal";
import CustomTable from "../../../components/ui/CustomTable";
import useModal from "../../../hooks/useModal";
import useTable from "../../../hooks/useTable";
import AdminLayout from "../../../layouts/AdminLayout";
import { formatDate, formatTime } from "../../../lib/time";
import {
  getAllPublicationAsync,
  updatePublicationAsync,
} from "../../../store/features/contentManagment/contentManagmentApi";
import { useAppDispatch, useAppSelector } from "../../../store/store";
import { TableData } from "../../../types";
import { GetContentData } from "../../../types/contentManagment/contentManagment";
import { countRow } from "../../../utils/number";
import { addKeyInArray, changeStatus, skipData } from "../../../utils/utils";

type Status =
  | "Published"
  | "CSO-Admin-Approved"
  | "Draft"
  | "Waiting_For_Approval"
  | "Archived"
  | "Back_To_Author";

const dealerStatusClass = {
  Published: "status-done",
  "CSO-Admin-Approved": "status-done",
  Draft: "status-draft",
  "Waiting for Publish": "status-info",
  Archived: "status-pending",
  Back_To_Author: "status-waiting",
  Waiting_For_Approval: "status-danger",
};

const changeStatused = (status: Status) => {
  // Optional formatter (e.g., replace hyphens with spaces and capitalize)
  return status.replace(/-/g, " ");
};

export default function AllContent() {
  const dispatch = useAppDispatch();
  const { open, toggle, handleOpen } = useModal(reRender);
  const { limit, page, handlePagination, handleLimit } = useTable();
  const { ContentList, countContent, loading } = useAppSelector(
    (state) => state.publication
  );

  const columns: ColumnsType<TableData<GetContentData>> = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, idx) => countRow(page, limit, idx),
    },
    {
      title: "Title",
      dataIndex: "publicationTitle",
      width: 312,
      render: (val) => (
        <p className="!whitespace-break-spaces">{val || "N/A"}</p>
      ),
    },
    {
      title: "Category",
      dataIndex: "categoryId",
      render: (val) => <span>{val?.categoryName || "N/A"}</span>,
    },
    {
      title: "Published By",
      dataIndex: "publicationAuthor",
      render: (val) => (
        <div>
          <span>{val?.fullName || "N/A"}</span>
        </div>
      ),
    },
    {
      title: "Date",
      dataIndex: "createdAt",
      render: (val) => (
        <div>
          <p>{formatDate(val)}</p>
          <p>{formatTime(val)}</p>
        </div>
      ),
    },
    {
      title: "Status",
      align: "center",
      dataIndex: "publicationStatus",
      render: (val: Status) => (
        <span className={dealerStatusClass[val]}>{changeStatused(val)}</span>
      ),
    },

    {
      title: "Action",
      align: "center",
      width: 100,
      render: (val) => (
        <Space size="small">
          <Link to={`/admin/content-manage/content/content-view/${val?._id}`}>
            <ActionButton
              icon={<ViewIcon />}
              onClick={() =>
                handleOpen({
                  type: "VIEW",
                  data: val,
                })
              }
            />
          </Link>

          <Link
            to={`/admin/content-manage/content/add-publication/${val?._id}`}
          >
            <ActionButton
              icon={<EditIcon />}
              onClick={() =>
                handleOpen({
                  type: "EDIT",
                  data: val,
                })
              }
            />
          </Link>

          <ActionButton
            icon={<ArchiveIcon />}
            onClick={() => handleArchive(val)}
          />
        </Space>
      ),
    },
  ];

  // item delete handler
  const handleDelete = (id: string) => {};

  // dispatch content
  const getData = useCallback(() => {
    dispatch(
      getAllPublicationAsync({
        params: {
          skip: skipData(page, limit),
          limit: limit,
        },
      })
    );
  }, [dispatch, limit, page]);

  const handleArchive = (data: GetContentData) => {
    // Prepare update data with all existing fields
    const updateData = {
      _id: data._id,
      publicationStatus: "Archived",
      publicationTitle: data.publicationTitle,
      categoryId: data.categoryId._id, // Only send the category ID
      publicationThumbnail: data.publicationThumbnail,
      publicationFiles: data?.publicationFiles ?? [],
      publicationContent: data.publicationContent,
    };

    dispatch(
      updatePublicationAsync({
        data: updateData,
        others: {
          reRender,
          id: data?._id,
        },
      })
    );
  };

  useEffect(() => {
    getData();
  }, [getData]);

  function reRender() {
    getData();
  }

  return (
    <AdminLayout
      title="Content a Publication"
      className="dashboard"
      headerChildren={
        <Link to={`/admin/content-manage/content/add-publication`}>
          <Button
            className="primary_btn"
            type="primary"
            size="large"
            icon={<AddIcon />}
            onClick={() =>
              handleOpen({
                type: "ADD",
              })
            }
          >
            Create New Content
          </Button>
        </Link>
      }
    >
      <CustomTable<TableData<GetContentData>>
        loading={loading}
        total={countContent}
        limit={limit}
        page={page}
        handlePagination={handlePagination}
        handleLimit={handleLimit}
        dataList={addKeyInArray(ContentList)}
        columns={columns}
        bordered={false}
        isLimitRow
        showTotalPage
      />

      {/* Category delete modal */}
      {open?.type === "DELETE" && (
        <ConfirmModal
          open={open.open}
          data={open?.data}
          deleteLoading={false}
          handleYes={handleDelete}
          handleClose={toggle}
        />
      )}
    </AdminLayout>
  );
}
